import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtMoney, formatApiError, todayLocal } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Calculator } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";

export default function JournalEntries() {
    const { currentOrg } = useAuth();
    const [items, setItems] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [open, setOpen] = useState(false);
    const today = todayLocal();
    const blank = { date: today, memo: "", lines: [{ account_id: "", debit: 0, credit: 0, description: "" }, { account_id: "", debit: 0, credit: 0, description: "" }] };
    const [form, setForm] = useState(blank);

    const load = async () => {
        const [{ data: je }, { data: ac }] = await Promise.all([api.get("/journal-entries"), api.get("/accounts")]);
        setItems(je.items || []); setAccounts(ac.items || []);
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    const setLine = (i, k, v) => {
        const lines = [...form.lines];
        lines[i] = { ...lines[i], [k]: v };
        setForm({ ...form, lines });
    };
    const addLine = () => setForm({ ...form, lines: [...form.lines, { account_id: "", debit: 0, credit: 0, description: "" }] });

    const debits = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
    const credits = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);

    const submit = async () => {
        try {
            await api.post("/journal-entries", form);
            toast.success("Journal entry posted");
            setOpen(false); setForm(blank); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <PageHeader
                subtitle="Accounting"
                title="Journal entries"
                action={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><Button className="bg-ocean-700 text-white rounded-xl" data-testid="new-je-btn"><Plus className="h-4 w-4 mr-1.5" /> New entry</Button></DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader><DialogTitle>New journal entry</DialogTitle></DialogHeader>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div><Label>Date</Label><Input className="mt-1.5" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                                <div><Label>Memo</Label><Input className="mt-1.5" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} /></div>
                            </div>
                            <div className="mt-4">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Lines</div>
                                <div className="grid grid-cols-12 gap-2 px-1 pb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                    <div className="col-span-4">Account</div>
                                    <div className="col-span-4">Description</div>
                                    <div className="col-span-2">Debit</div>
                                    <div className="col-span-2">Credit</div>
                                </div>
                                {form.lines.map((l, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                                        <Select value={l.account_id} onValueChange={(v) => setLine(i, "account_id", v)}>
                                            <SelectTrigger className="col-span-4"><SelectValue placeholder="Account" /></SelectTrigger>
                                            <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                        <Input className="col-span-4" placeholder="Optional narration" value={l.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} />
                                        <Input className="col-span-2" type="number" placeholder="Debit" value={l.debit} onChange={(e) => setLine(i, "debit", Number(e.target.value))} />
                                        <Input className="col-span-2" type="number" placeholder="Credit" value={l.credit} onChange={(e) => setLine(i, "credit", Number(e.target.value))} />
                                    </div>
                                ))}
                                <Button size="sm" variant="outline" onClick={addLine} className="rounded-lg"><Plus className="h-4 w-4 mr-1" /> Add line</Button>
                            </div>
                            <div className="rounded-xl bg-sand-100 p-4 mt-3 grid grid-cols-3 text-sm">
                                <div><div className="text-muted-foreground text-xs">Debits</div><div className="font-mono">{fmtMoney(debits, currentOrg?.currency)}</div></div>
                                <div><div className="text-muted-foreground text-xs">Credits</div><div className="font-mono">{fmtMoney(credits, currentOrg?.currency)}</div></div>
                                <div><div className="text-muted-foreground text-xs">Difference</div><div className={`font-mono ${Math.abs(debits - credits) < 0.005 ? "text-emerald-700" : "text-coral-dark"}`}>{fmtMoney(debits - credits, currentOrg?.currency)}</div></div>
                            </div>
                            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={Math.abs(debits - credits) > 0.005} className="bg-ocean-700 text-white">Post</Button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            {items.length === 0 ? (
                <EmptyState icon={Calculator} title="No journal entries yet" description="Post manual adjustments, accruals, or corrections here." />
            ) : (
                <div className="space-y-3">
                    {items.map((e) => (
                        <div key={e.id} className="rounded-3xl bg-white border border-sand-200 shadow-card p-5">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="font-mono text-xs text-muted-foreground">{e.number}</div>
                                    <div className="font-medium text-ocean-700 mt-1">{e.memo}</div>
                                    <div className="text-xs text-muted-foreground">{fmtDate(e.date)}</div>
                                </div>
                            </div>
                            <table className="w-full text-sm mt-4">
                                <thead className="text-xs uppercase text-muted-foreground"><tr><th className="text-left py-1">Account</th><th className="text-right py-1">Debit</th><th className="text-right py-1">Credit</th></tr></thead>
                                <tbody className="divide-y divide-sand-200">
                                    {e.lines.map((l, i) => {
                                        const acc = accounts.find((a) => a.id === l.account_id);
                                        return (
                                            <tr key={i}>
                                                <td className="py-2">{acc ? `${acc.code} — ${acc.name}` : "—"}</td>
                                                <td className="py-2 font-mono text-right">{l.debit ? fmtMoney(l.debit, currentOrg?.currency) : ""}</td>
                                                <td className="py-2 font-mono text-right">{l.credit ? fmtMoney(l.credit, currentOrg?.currency) : ""}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
